import { createBrowserRouter, Navigate, Outlet, type RouteObject } from "react-router";
import { RequireAuth } from "@/shared/auth/RequireAuth";
import { Spinner } from "@/shared/ui";
import { AppLayout } from "./layout/AppLayout";
import { RouteError } from "./layout/RouteError";

// Each page is its own chunk: the first load only ships the shell + login.
const page = (load: () => Promise<{ default: React.ComponentType }>): Pick<RouteObject, "lazy"> => ({
  lazy: async () => ({ Component: (await load()).default }),
});

export const router = createBrowserRouter([
  { path: "/login", ...page(() => import("@/features/auth/pages/LoginPage")) },
  {
    path: "/",
    element: <RequireAuth><AppLayout /></RequireAuth>,
    errorElement: <RouteError />,
    HydrateFallback: Spinner,
    children: [
      { index: true, ...page(() => import("@/features/dashboard/pages/DashboardPage")) },
      { path: "courses", ...page(() => import("@/features/courses/pages/CoursesPage")) },
      { path: "courses/:id", ...page(() => import("@/features/courses/pages/CourseDetailPage")) },
      { path: "course-imports", ...page(() => import("@/features/courses/pages/CourseImportsPage")) },
      { path: "course-imports/:id", ...page(() => import("@/features/courses/pages/CourseImportPage")) },
      { path: "courses/:courseId/units/:unitId/theory", ...page(() => import("@/features/courses/pages/UnitTheoryPage")) },
      { path: "lessons/:id", ...page(() => import("@/features/lessons/pages/LessonEditorPage")) },
      { path: "vocabulary", ...page(() => import("@/features/vocabulary/pages/VocabularyPage")) },
      { path: "hanviet", ...page(() => import("@/features/hanviet/pages/HanVietPage")) },
      { path: "readings", ...page(() => import("@/features/readings/pages/ReadingsPage")) },
      { path: "readings/new", ...page(() => import("@/features/readings/pages/ReadingDraftPage")) }, // AI group G
      { path: "readings/:id", ...page(() => import("@/features/readings/pages/ReadingEditorPage")) },
      { path: "promos", ...page(() => import("@/features/promos/pages/PromosPage")) },
      { path: "promos/new", ...page(() => import("@/features/promos/pages/PromoEditorPage")) },
      { path: "promos/:id", ...page(() => import("@/features/promos/pages/PromoEditorPage")) },
      { path: "results", ...page(() => import("@/features/results/pages/ResultsPage")) },
      { path: "essays", ...page(() => import("@/features/essays/pages/EssaysPage")) },
      { path: "languages", ...page(() => import("@/features/languages/pages/LanguagesPage")) },
      { path: "ai/roleplays", ...page(() => import("@/features/ai-speaking/pages/AiRoleplaysPage")) }, // AI group E (speaking)
      // Language centers: the center dashboard (center admins; admins for support).
      {
        element: <RequireAuth roles={["center_admin", "admin"]}><Outlet /></RequireAuth>,
        children: [
          { path: "center", ...page(() => import("@/features/center/pages/CenterHomePage")) },
          { path: "center/:orgId", ...page(() => import("@/features/center/pages/CenterOrgPage")) },
        ],
      },
      { path: "pronunciation", ...page(() => import("@/features/pronunciation/pages/PronunciationInsightsPage")) },
      {
        element: <RequireAuth roles={["admin"]}><Outlet /></RequireAuth>,
        children: [
          { path: "staff", ...page(() => import("@/features/staff/pages/StaffPage")) },
          { path: "users", ...page(() => import("@/features/users/pages/UsersPage")) },
          { path: "users/:id", ...page(() => import("@/features/users/pages/UserDetailPage")) },
          { path: "classrooms", ...page(() => import("@/features/classrooms/pages/ClassroomsPage")) },
          { path: "classrooms/:id", ...page(() => import("@/features/classrooms/pages/ClassroomDetailPage")) },
          { path: "orgs", ...page(() => import("@/features/orgs/pages/OrgsPage")) },
          { path: "orgs/:id", ...page(() => import("@/features/orgs/pages/OrgDetailPage")) },
          { path: "org-pricing", ...page(() => import("@/features/orgs/pages/OrgPricingPage")) },
          { path: "friend-reports", ...page(() => import("@/features/users/pages/FriendReportsPage")) },
          { path: "notifications", ...page(() => import("@/features/notifications/pages/NotificationsPage")) },
          { path: "notifications/automations/new", ...page(() => import("@/features/notifications/pages/AutomationEditorPage")) },
          { path: "notifications/automations/:id", ...page(() => import("@/features/notifications/pages/AutomationEditorPage")) },
          { path: "feedback", ...page(() => import("@/features/feedback/pages/FeedbackPage")) },
          { path: "billing", ...page(() => import("@/features/billing/pages/BillingPage")) },
          { path: "analytics", ...page(() => import("@/features/analytics/pages/AnalyticsPage")) },
          { path: "audit", ...page(() => import("@/features/audit/pages/AuditPage")) },
          { path: "ai", ...page(() => import("@/features/ai/pages/AiSettingsPage")) },
          { path: "ai/providers", ...page(() => import("@/features/ai/pages/AiProvidersPage")) },
          { path: "ai/features", ...page(() => import("@/features/ai/pages/AiFeaturesPage")) },
          { path: "ai/features/:key", ...page(() => import("@/features/ai/pages/AiFeatureEditorPage")) },
          { path: "ai/usage", ...page(() => import("@/features/ai/pages/AiUsagePage")) },
          { path: "leagues", ...page(() => import("@/features/leagues/pages/LeaguesPage")) }, // leagues & quests
          { path: "leagues/cohorts/:id", ...page(() => import("@/features/leagues/pages/LeagueCohortPage")) },
          { path: "quest-templates", ...page(() => import("@/features/leagues/pages/QuestTemplatesPage")) },
          { path: "ai/voice", ...page(() => import("@/features/ai-voice/pages/AiVoicePage")) }, // ai-voice-reader
        ],
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
